import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { CompaniesFacade } from 'mbs-ui-shared@facades/companies.facade';
import { Company, CompanyDestination, LicensesSettings } from 'mbs-ui-shared@models';
import { POST_PAYMENT_TOKEN } from 'mbs-ui-shared@tokens/post-payment.token';
import { Observable, tap } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class CompanyWizardFacade {
  isPostPayment = inject<Observable<boolean>>(POST_PAYMENT_TOKEN);
  private facade = inject(CompaniesFacade);

  public readonly company: WritableSignal<Company> = signal(null);
  public readonly createdCompany: WritableSignal<boolean> = signal(false);
  public readonly loading: WritableSignal<boolean> = signal(false);

  public reset(): void {
    this.company.set(null);
    this.createdCompany.set(false);
    this.loading.set(false);
  }

  public addDestinations(destinations: CompanyDestination[]): Observable<Company> {
    const company = this.company();

    return this.facade
      .update({ ...company, inheritedDestinations: [...(company.inheritedDestinations ?? []), ...destinations] } as Company)
      .pipe(switchMap(() => this.reloadCompany()));
  }

  public updateCompanyName(name: string): Observable<Company> {
    return this.facade.update({ ...this.company(), name } as Company).pipe(switchMap(() => this.reloadCompany()));
  }

  public reloadCompany(): Observable<Company> {
    return this.facade.getCompany(this.company().id).pipe(tap((company) => this.company.set(company)));
  }

  public createCompany(name: string): Observable<Company> {
    const company = { name } as Company;

    return this.isPostPayment.pipe(
      take(1),
      switchMap((isPostPayment) => {
        if (isPostPayment && !company.licensesSettings) company.licensesSettings = LicensesSettings.GlobalPool;

        return this.facade.create(company);
      })
    );
  }
}
